import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /features/accounting - prerendered feature page targeting searches like
 * "school accounting software Zimbabwe" and "Sage Pastel alternative for schools".
 */
export const AccountingFeaturePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/accounting">
      <SEO
        title="School Accounting Software for Zimbabwean Schools | Rongeka Africa"
        description="Full double-entry accounting built for school bursars: trial balance, income statement, bank reconciliation, budgets, and year-end reports - with fee income posting into your books automatically."
        url="https://rongeka.africa/features/accounting"
        keywords="school accounting software Zimbabwe, school bursar software, Sage Pastel alternative for schools, school financial management system, double-entry accounting schools, school budgeting software"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">ACCOUNTING</div>
        <h1 className="mkt-hero-title">Full School Accounting, Connected to Your Fees</h1>
        <p className="mkt-hero-lead">
          Everything your bursar runs in Sage Pastel or QuickBooks, inside the same system
          that collects your fees. Fee income posts into your books automatically - no
          double capture, no separate licence.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Why schools leave standalone accounting packages</h2>
          <p className="mkt-section-body">
            In most schools, the bursar records a fee payment in one system, then captures the
            same payment again in Sage Pastel at month end. Every re-entry is a chance for the
            books and the fee records to disagree. Rongeka closes that gap: when a payment is
            receipted against a student, the entry lands in your ledger at the same moment.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">A complete bookkeeping suite for schools</h2>
          <div className="mkt-grid">
            <div className="mkt-card">
              <h3>Books and reporting</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Double-entry general ledger and journals</li>
                <li><CheckCircleOutlined />Trial balance, income statement, and balance sheet</li>
                <li><CheckCircleOutlined />End-of-year reports ready for your auditor or board</li>
              </ul>
            </div>
            <div className="mkt-card">
              <h3>Money in and out</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Bank reconciliation against your payment records</li>
                <li><CheckCircleOutlined />Expense tracking by department or cost centre</li>
                <li><CheckCircleOutlined />Creditors and purchase requisitions with approvals</li>
              </ul>
            </div>
            <div className="mkt-card">
              <h3>Planning and control</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Budgets tracked against actual spending</li>
                <li><CheckCircleOutlined />Fixed asset register</li>
                <li><CheckCircleOutlined />Full audit trail of every entry</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Built around how school money actually moves</h2>
          <p className="mkt-section-body">
            School accounting is not shop accounting. Income arrives as tuition and levies per
            student per term, and boards want to see collections against arrears - not just a
            revenue line. Because the accounting module sits on top of Rongeka's{' '}
            <RouterLink to="/features/fees">fees management</RouterLink>, your books always
            reconcile with what parents actually paid, including{' '}
            <RouterLink to="/features/ecocash-payments">EcoCash payments</RouterLink> that
            receipt themselves.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Fee income posts automatically when a payment is recorded</li>
            <li><CheckCircleOutlined />Levies and charges mapped to the accounts you choose</li>
            <li><CheckCircleOutlined />Collections, arrears, and expenditure in one set of reports</li>
            <li><CheckCircleOutlined />No separate accounting licence to buy or renew</li>
          </ul>
        </div>
      </section>
    </MarketingLayout>
  );
};
